import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import moment from 'moment';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import AccountingWebAPIUtils from 'utils/AccountingWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import formatMoney from 'utils/formatters/Money';
import { isValidStatus } from 'helpers/accounting';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { fetchSignedUrlByForeignKey } from '../../../../../utils/DocumentsAPIUtils';
import { sluggify } from '../../../../../utils/localization/utils';
import SendEmailModal from '../../send-email-modal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { BadgeAlt as Badge, Button, Card, CardHeader, CardContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from '@cargomatic/ui';
import { Download, Mail, ExternalLink, ChevronDown, RefreshCw, CheckCircle, XCircle, CircleCheck, CircleDollarSign, CreditCard, Ban, Clock, } from 'lucide-react';
const BADGE_COLOR_MAP = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    brand: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-700',
};
const PAYOUT_STATUS_COLOR = {
    approved_internally: 'green',
    payment_initialized: 'blue',
    paid: 'brand',
    void: 'red',
};
const STATUS_ICON_MAP = {
    approved_internally: CircleCheck,
    payment_initialized: CreditCard,
    paid: CircleDollarSign,
    void: Ban,
    internal_review: Clock,
};
const PayoutHeader = ({ payout, isModal, onPayoutUpdated, }) => {
    const { t } = useTranslation();
    const [showSendEmailModal, setShowSendEmailModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showVoidModal, setShowVoidModal] = useState(false);
    const [isReprocessing, setIsReprocessing] = useState(false);
    const handleDownload = async () => {
        const newWindow = window.open('', '_blank');
        try {
            const { url, error } = await fetchSignedUrlByForeignKey(payout._id);
            if (url) {
                if (newWindow) {
                    newWindow.opener = null;
                    newWindow.location.href = url;
                }
            }
            else {
                newWindow === null || newWindow === void 0 ? void 0 : newWindow.close();
                showToast(t('GET_SIGNED_URL_ERROR'), error, 'error');
            }
        }
        catch (e) {
            newWindow === null || newWindow === void 0 ? void 0 : newWindow.close();
            showToast(t('GET_SIGNED_URL_ERROR'), e.message, 'error');
        }
    };
    const sendEmail = (to) => {
        AccountingActionCreators.emailPayout(payout._id, to);
    };
    const approvePayout = () => {
        setShowApprovalModal(false);
        AccountingActionCreators.approvePayout(payout._id);
    };
    const reverseApproval = () => {
        setShowApprovalModal(false);
        AccountingActionCreators.reversePayoutApproval(payout._id);
    };
    const voidPayout = () => {
        setShowVoidModal(false);
        AccountingActionCreators.voidPayout(payout._id);
    };
    const reverseVoid = () => {
        setShowVoidModal(false);
        AccountingActionCreators.reversePayoutVoid(payout._id);
    };
    const reprocessCarrierPayout = async () => {
        var _a;
        setIsReprocessing(true);
        const response = await AccountingWebAPIUtils.reprocessCarrierPayout(payout._id).catch((entity) => {
            const err = entity.error;
            if (err) {
                showToast(t('REPROCESS_ERROR'), t('ERROR_REPROCESSING_WITH_VALUE', { error: err }), 'error');
            }
        });
        setIsReprocessing(false);
        if (response && response.payout) {
            const updatedPayout = Object.assign(Object.assign({}, response.payout), { jobs: ((_a = response.payout.jobs) !== null && _a !== void 0 ? _a : []).map(({ _id }) => _id) });
            onPayoutUpdated(updatedPayout);
            showToast(t('REPROCESS_SUCCESS'), t('SUCCESSFULLY_REPROCESSED_PAYOUT'), 'success');
        }
    };
    const { status } = payout;
    const payoutStatusColor = PAYOUT_STATUS_COLOR[status] || 'gray';
    const approvalAction = status === 'approved_internally' ? 'Reverse Approval' : 'Approve';
    const approvalConfirm = status === 'approved_internally' ? reverseApproval : approvePayout;
    const showApprovalAction = status !== 'payment_initialized' && status !== 'paid' && status !== 'void';
    const voidAction = status === 'void' ? 'Reverse Void' : 'Void';
    const voidConfirm = status === 'void' ? reverseVoid : voidPayout;
    const showVoidAction = status !== 'approved_internally' &&
        status !== 'payment_initialized' &&
        status !== 'paid';
    return (React.createElement(React.Fragment, null,
        React.createElement(Card, null,
            React.createElement(CardHeader, { className: "space-y-4" },
                React.createElement("div", { className: "flex items-start justify-between gap-4" },
                    React.createElement("div", { className: "space-y-2 min-w-0 flex-1" },
                        React.createElement("div", { className: "flex items-center gap-3 flex-wrap" },
                            React.createElement("h1", { "data-testid": "h2-bede8f08", className: "text-3xl font-bold tracking-tight my-0" }, payout.number),
                            isValidStatus(payout.status) && (React.createElement(Badge, { variant: "outline", "data-testid": "CargomaticBadge-4b4cde3d", className: `gap-1 ${BADGE_COLOR_MAP[payoutStatusColor] || ''}` },
                                (() => {
                                    const StatusIcon = STATUS_ICON_MAP[payout.status] || Clock;
                                    return React.createElement(StatusIcon, { className: "h-3.5 w-3.5" });
                                })(),
                                t(sluggify(payout.status))))),
                        React.createElement("p", { className: "text-sm text-muted-foreground my-0" },
                            t('PAYOUT'),
                            " for ",
                            unescapeSpecialChars(payout.carrier.name))),
                    React.createElement(DropdownMenu, null,
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: "outline", className: "shrink-0 gap-2" },
                                t('ACTIONS'),
                                React.createElement(ChevronDown, { className: "h-4 w-4" }))),
                        React.createElement(DropdownMenuContent, { align: "end" },
                            showApprovalAction && (React.createElement(DropdownMenuItem, { className: "gap-2", onClick: () => setShowApprovalModal(true) },
                                status === 'approved_internally' ? (React.createElement(RefreshCw, { className: "h-4 w-4" })) : (React.createElement(CheckCircle, { className: "h-4 w-4" })),
                                t(sluggify(approvalAction)))),
                            showVoidAction && (React.createElement(DropdownMenuItem, { className: "gap-2", onClick: () => setShowVoidModal(true) },
                                React.createElement(XCircle, { className: "h-4 w-4" }),
                                t(sluggify(voidAction)))),
                            React.createElement(AuthorizationWrapper, { excludedRegions: [REGIONS.AP] },
                                React.createElement(DropdownMenuItem, { className: "gap-2", onClick: () => setShowSendEmailModal(true) },
                                    React.createElement(Mail, { className: "h-4 w-4" }),
                                    titleize(t('EMAIL')))),
                            React.createElement(DropdownMenuItem, { className: "gap-2", onClick: handleDownload },
                                React.createElement(Download, { className: "h-4 w-4" }),
                                t('DOWNLOAD')),
                            isModal && (React.createElement(DropdownMenuItem, { className: "gap-2", onClick: () => window.open(`/payouts/${payout._id}`, '_blank', 'noopener,noreferrer') },
                                React.createElement(ExternalLink, { className: "h-4 w-4" }),
                                t('OPEN_IN_NEW_TAB'))),
                            React.createElement(DropdownMenuItem, { className: "gap-2", onClick: reprocessCarrierPayout, disabled: payout.status !== 'internal_review' || isReprocessing },
                                React.createElement(RefreshCw, { className: "h-4 w-4" }),
                                t('REPROCESS_PAYOUT')))))),
            React.createElement(CardContent, { className: "flex flex-wrap items-center gap-8" },
                React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('TOTAL'))),
                    React.createElement("span", { className: "text-2xl font-bold" }, formatMoney(payout.total, payout.currency))),
                React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('PAID'))),
                    React.createElement("span", { className: "text-2xl font-bold" }, formatMoney(payout.total_payments, payout.currency))),
                React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('BALANCE'))),
                    React.createElement("span", { className: "text-2xl font-bold text-emerald-600" }, formatMoney(payout.amount_due, payout.currency))),
                payout.due_date && (React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('DUE_DATE'))),
                    React.createElement("span", { className: "text-2xl font-bold" }, moment(payout.due_date).format('LL')))))),
        React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-c0408354", show: showApprovalModal, title: `${t(sluggify(approvalAction))} ${payout.number}`, onConfirm: approvalConfirm, onHide: () => setShowApprovalModal(false), message: t('ARE_YOU_SURE'), hideButtonText: t('CANCEL'), affirmative: t('OK') }),
        React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-12c14055", show: showVoidModal, title: `${t(sluggify(voidAction))} ${payout.number}`, onConfirm: voidConfirm, onHide: () => setShowVoidModal(false), message: t('ARE_YOU_SURE'), hideButtonText: t('CANCEL'), affirmative: t('OK') }),
        React.createElement(SendEmailModal, { show: showSendEmailModal, onSend: sendEmail, onHide: () => setShowSendEmailModal(false) })));
};
export default PayoutHeader;
